/**
 * TSEDashboard2
 *
 * Two-pane layout identical to TSECopilotDashboard, with one difference:
 *   LEFT  pane  — Customer Chat  →  sockets  (unchanged)
 *   RIGHT pane  — Copilot        →  REST proxy via Cognito Bearer token
 *                                   (POST /api/copilot/chat — backend uses the
 *                                    agent's primary API key internally, no key
 *                                    ever stored in the browser)
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connectSocket } from "../socket";
import SideBar from "./SideBar";
import ImagePreviewModal from "./ImagePreviewModal";
import UserProfileMenu from "./UserProfileMenu";
import FeedbackModal from "./FeedbackModal";
import { authFetch, openAuthenticatedFile, isServerProxyDocUrl } from "../utils/auth";
import { Zap } from "lucide-react";
import { DiagnosticMapWindow } from "./tse";

// ── Knowledge-graph cache endpoint ─────────────────────────────────────────
// The backend serves the full product graph ONCE per session via this endpoint.
// Chat responses no longer embed diagnostic_subgraph / session_snapshot.
const KNOWLEDGE_GRAPH_ENDPOINT = (product) => `/api/knowledge-graph/${encodeURIComponent(product)}`;

// Cache-hit badge. Blue and solid-filled so it reads as a bolt rather than as
// one more muted glyph — kept in sync with .d5-cache-indicator in dashboard5.css.
const CacheIndicator = () => (
  <span className="cache-indicator" title="Response served from cache">
    <Zap size={11} strokeWidth={2} />
  </span>
);

// ── Export to Markdown ────────────────────────────────────────────────────────

function downloadSessionMarkdown(dashboardMessages, sessionStats) {
  const lines = ["# Copilot Session", ""];
  if (sessionStats.turns > 0) {
    let summary = `**Turns:** ${sessionStats.turns}`;
    if (sessionStats.totalLatencyMs > 0) summary += ` · **Total latency:** ${(sessionStats.totalLatencyMs / 1000).toFixed(1)}s`;
    if (sessionStats.totalCostUsd > 0) summary += ` · **Total cost:** $${sessionStats.totalCostUsd.toFixed(4)}`;
    lines.push(summary, "", "---");
  } else {
    lines.push("---");
  }

  const skip = new Set(["rag_searching", "rag_results", "session_clear_divider"]);
  for (const msg of dashboardMessages) {
    if (skip.has(msg.type)) continue;
    const ts = msg.timestamp ? new Date(msg.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
    lines.push("");
    if (msg.type === "answered_question" && msg.questionSnapshot) {
      lines.push(`## 🤖 Copilot${ts ? ` — ${ts}` : ""}`);
      lines.push("", msg.questionSnapshot.question || "");
      if (msg.selectedAnswer) lines.push("", `**Your answer:** ${msg.selectedAnswer}`);
    } else {
      const label = msg.sender === "agent" ? "👤 You" : msg.sender === "user" ? "👤 Customer" : "🤖 Copilot";
      lines.push(`## ${label}${ts ? ` — ${ts}` : ""}`);
      lines.push("", msg.text || "");
    }
    lines.push("", "---");
  }

  lines.push("", "*Exported from Arta Copilot*");
  const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `copilot-session-${new Date().toISOString().slice(0, 10)}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Shared helpers ────────────────────────────────────────────────────────────

const isImagePreviewLink = (label = "", url = "") => {
  const l = label.trim().toLowerCase();
  const u = url.trim().toLowerCase();
  return (
    l.includes("image") || l.includes("screenshot") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(u) ||
    u.includes("drive.google.com/file/d/")
  );
};

// ── Question widgets ──────────────────────────────────────────────────────────

const MultiSelectWidget = ({ options, onSubmit, formatBotMessage }) => {
  const [selected, setSelected] = useState(new Set());
  const toggle = (opt) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(opt)) next.delete(opt); else next.add(opt);
      return next;
    });
  return (
    <div>
      <div className="flex flex-col gap-2">
        {options.map((opt, i) => (
          <button
            key={i}
            onClick={() => toggle(opt)}
            className={`text-left px-4 py-2.5 border rounded-lg text-sm transition flex items-center gap-2 ${selected.has(opt)
                ? "border-[#06b6d4] bg-cyan-50 text-cyan-800"
                : "border-gray-200 hover:border-[#06b6d4] hover:bg-cyan-50 text-[#253439]"
              }`}
          >
            <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected.has(opt) ? "border-[#06b6d4] bg-[#06b6d4]" : "border-gray-300"
              }`}>
              {selected.has(opt) && (
                <svg width="10" height="10" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              )}
            </span>
            <span dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }} />
          </button>
        ))}
      </div>
      <button
        onClick={() => onSubmit(JSON.stringify([...selected]))}
        disabled={selected.size === 0}
        className="mt-3 px-5 py-2 bg-[#06b6d4] hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Submit ({selected.size} selected)
      </button>
    </div>
  );
};

const TextQuestionWidget = ({ onSubmit }) => {
  const [value, setValue] = useState("");
  return (
    <div>
      <textarea
        className="w-full border border-gray-200 rounded-lg p-3 text-sm text-[#253439] placeholder-gray-400 focus:border-[#06b6d4] focus:ring-1 focus:ring-[#06b6d4] outline-none resize-none"
        style={{ minHeight: "60px", fontFamily: "'Space Grotesk', sans-serif" }}
        placeholder="Type your answer..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (value.trim()) onSubmit(value.trim()); }
        }}
        autoFocus
      />
      <button
        onClick={() => { if (value.trim()) onSubmit(value.trim()); }}
        disabled={!value.trim()}
        className="mt-2 px-5 py-2 bg-[#06b6d4] hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Submit
      </button>
    </div>
  );
};

const KNOWN_NODE_TYPES = new Set([
  "Product", "Problem", "Symptom", "Workflow", "Decision",
  "DiagnosticStep", "DiagnosticSteps", "PossibleCause",
  "Resolution", "Remediation", "Workaround", "Info",
]);

const inferTypeFromNid = (nid = "") => {
  const n = nid.toUpperCase();
  if (/^WF_/.test(n) || /^MAIN_/.test(n) || /_START/.test(n) || /^START_/.test(n)) return "Workflow";
  if (/^PC_/.test(n) || /_PC\d/.test(n) || /_PC_/.test(n) || /^CAUSE_/.test(n) || /POSSIBLE_CAUSE/.test(n)) return "PossibleCause";
  if (/^RES_/.test(n) || /_RES\d/.test(n) || /^RESOLUTION_/.test(n) || /^FIX_/.test(n)) return "Resolution";
  if (/^REM_/.test(n) || /^REMED_/.test(n) || /^RX_/.test(n)) return "Remediation";
  if (/^WORK_/.test(n) || /^WA_/.test(n) || /^WRK_/.test(n)) return "Workaround";
  if (/^DS_/.test(n) || /_DS\d/.test(n) || /^DIAG_/.test(n) || /^CHECK_/.test(n) || /^STEP_/.test(n)) return "DiagnosticStep";
  if (/^DEC_/.test(n) || /^Q_/.test(n) || /^Q\d+[A-Z]?$/.test(nid) || /_Q\d/.test(n)) return "Decision";
  if (/^PROB_/.test(n) || /^ROOT_/.test(n)) return "Problem";
  if (/^SYM_/.test(n) || /_SYM/.test(n) || /^S_/.test(n) || /^CC_/.test(n)) return "Symptom";
  return null;
};

const getDiagNodeFromCurrentNode = (node, fallback = {}) => {
  if (!node || typeof node !== "object") return null;
  const labels = node.labels || [];
  const id = node.nid || node.id || node.name || fallback.id;
  if (!id) return null;
  const type =
    node.type ||
    node.current_node_type ||
    labels.find((label) => KNOWN_NODE_TYPES.has(label)) ||
    inferTypeFromNid(id) ||
    fallback.type ||
    "Info";
  return {
    id,
    nid: node.nid || id,
    type,
    name: node.name || node.title || fallback.name || id,
    description: node.description || node.question || node.command || "",
    turn: fallback.turn,
  };
};

// ── Main Component ────────────────────────────────────────────────────────────

const TSEDashboard2 = ({ onLogout, username: loggedInUsername, embedded = false, initialQuery = null, onContextChange = null, aiAgentName = null }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // When embedded (e.g. inside the enterprise split view) we show only the
  // Copilot Dashboard pane, strip full-page chrome, and auto-run initialQuery.
  const autoRanRef = useRef(false);
  const socketRef = useRef(null);
  const agentName = localStorage.getItem("username") || "TSE Agent";
  // In the embedded enterprise (/ai-agent) view, present the AI assistant by the
  // same session persona name shown on the welcome screen (e.g. "Mike"), so the
  // two screens stay consistent. Standalone dashboard keeps "Copilot".
  const copilotLabel = embedded ? (aiAgentName || "AI Agent") : "Copilot";
  const restSessionIdRef = useRef(null);
  const firstPromptRef = useRef(null); // first agent message of the session — for feedback metadata
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackBlockedNotice, setFeedbackBlockedNotice] = useState("");
  const feedbackBlockedTimerRef = useRef(null);

  const openFeedbackModal = () => {
    if (!restSessionIdRef.current) {
      setFeedbackBlockedNotice("Ask a question first before sharing feedback.");
      if (feedbackBlockedTimerRef.current) window.clearTimeout(feedbackBlockedTimerRef.current);
      feedbackBlockedTimerRef.current = window.setTimeout(() => setFeedbackBlockedNotice(""), 4000);
      return;
    }
    setShowFeedbackModal(true);
  };
  const [leftPct, setLeftPct] = useState(32);
  const splitContainerRef = useRef(null);
  const isResizingRef = useRef(false);
  const [zoomImageUrl, setZoomImageUrl] = useState(null);

  const [customerMessage, setCustomerMessage] = useState("");
  const [customerMessages, setCustomerMessages] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const customerChatBodyRef = useRef(null);

  const [dashboardMessage, setDashboardMessage] = useState("");
  const [dashboardMessages, setDashboardMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isQuestionCollapsed, setIsQuestionCollapsed] = useState(false);
  const [implicitCases, setImplicitCases] = useState([]);
  const [implicitBugs, setImplicitBugs] = useState([]);
  const [hasNewCases, setHasNewCases] = useState(false);
  const [hasNewBugs, setHasNewBugs] = useState(false);
  const [showCasesDropdown, setShowCasesDropdown] = useState(false);
  const [showBugsDropdown, setShowBugsDropdown] = useState(false);
  const [loadingMoreCases, setLoadingMoreCases] = useState(false);
  const [loadingMoreBugs, setLoadingMoreBugs] = useState(false);
  const [waitingForAnswer, setWaitingForAnswer] = useState(false);
  const [dashboardSendError, setDashboardSendError] = useState("");
  const dashboardSendErrorTimerRef = useRef(null);
  const dashboardChatBodyRef = useRef(null);
  const clearDividerRef = useRef(null);
  const dashboardTextareaRef = useRef(null);
  const customerTextareaRef = useRef(null);
  const [shouldScrollToDivider, setShouldScrollToDivider] = useState(false);
  const [diagMapTrail, setDiagMapTrail] = useState([]);
  const [diagMapSnapshot, setDiagMapSnapshot] = useState(null);
  const [diagMapFullSnapshot, setDiagMapFullSnapshot] = useState(null);
  const [diagMapMaxTurn, setDiagMapMaxTurn] = useState(null);
  const diagMapTurnRef = useRef(0);
  const copilotTurnRef = useRef(0);
  const latestCopilotMsgRef = useRef(null);
  const [showScrollArrow, setShowScrollArrow] = useState(false);
  const copilotAbortRef = useRef(null);
  const [diagMapInlineOpen, setDiagMapInlineOpen] = useState(false);
  const [diagMapInlineMinimized, setDiagMapInlineMinimized] = useState(false);
  const [diagMapFullOpen, setDiagMapFullOpen] = useState(false);
  const [diagMapFullMinimized, setDiagMapFullMinimized] = useState(false);
  const [diagMapFocusNodeId, setDiagMapFocusNodeId] = useState(null);
  const [diagMapFocusKey, setDiagMapFocusKey] = useState(0);
  const [diagMapFullFocusNodeId, setDiagMapFullFocusNodeId] = useState(null);
  const [diagMapFullFocusKey, setDiagMapFullFocusKey] = useState(0);
  const diagMapOpenRef = useRef(false);
  const [sessionStats, setSessionStats] = useState({ turns: 0, totalCostUsd: 0, totalLatencyMs: 0, turnLog: [] });
  const resetSessionStats = () => setSessionStats({ turns: 0, totalCostUsd: 0, totalLatencyMs: 0, turnLog: [] });

  // Report the Copilot's findings upward so the enterprise (/ai-agent) Search Mode
  // pane can run a deeper, fact-aware research using this diagnostic context.
  useEffect(() => {
    if (!onContextChange) return;
    const facts = dashboardMessages
      .filter((m) => m.sender === "copilot" && m.text)
      .map((m) => m.text)
      .join("\n\n");
    onContextChange(facts);
  }, [dashboardMessages, onContextChange]);

  // ── Knowledge-graph cache ───────────────────────────────────────────────
  // fullGraphRef holds { nodes: [...], edges: [...] } fetched once per product.
  // graphFetchedForProductRef tracks which product is currently cached so we
  // refetch when the product changes within a session.
  // lastChatDataRef holds the most recent payload so we can re-apply the
  // snapshot once the graph fetch resolves after messages have already arrived.
  const fullGraphRef = useRef(null);
  const graphFetchedForProductRef = useRef(null);
  const lastChatDataRef = useRef(null);
  const [graphLoading, setGraphLoading] = useState(false);

  const recordDiagMapNode = useCallback((node) => {
    if (!node) return;
    const turn = ++diagMapTurnRef.current;
    setDiagMapTrail((prev) => {
      const id = node.nid || node.id || `${node.type || "node"}-${Date.now()}`;
      const last = prev[prev.length - 1];
      if (last && (last.nid || last.id) === id) return prev;
      return [...prev, { ...node, id, nid: node.nid || id, turn }].slice(-40);
    });
    return turn;
  }, []);

  const openDiagMapFullWindow = useCallback(() => {
    setDiagMapFullFocusNodeId(null);
    setDiagMapFullFocusKey(0);
    setDiagMapFullOpen(true);
    setDiagMapFullMinimized(false);
  }, []);

  const openInlineDiagMapForNode = useCallback((nodeId, maxTurn = null) => {
    setDiagMapFocusNodeId(nodeId || null);
    setDiagMapFocusKey(k => k + 1);
    setDiagMapMaxTurn(maxTurn);
    setDiagMapFullOpen(false);
    setDiagMapFullMinimized(false);
    setDiagMapInlineOpen(true);
    setDiagMapInlineMinimized(false);
  }, []);

  const advanceDiagMapSnapshot = useCallback((newSnap) => {
    if (!newSnap) return;
    // Full map must ACCUMULATE — never let a partial/cache-hit turn (which can
    // return only a few visited nodes, or none) shrink the path already shown.
    // DiagnosticMapWindow scopes its render to traversal_path/visited_nodes, so a
    // raw overwrite with a short path collapses the map. Union the visited info and
    // keep the longer traversal_path, mirroring the mini-view merge below. The graph
    // is the same full product graph each turn, so carry the newest (or keep prev).
    setDiagMapFullSnapshot((prev) => {
      if (!prev) return newSnap;
      const visited_nodes = [...new Set([...(prev.visited_nodes || []), ...(newSnap.visited_nodes || [])])];
      const prevPath = prev.traversal_path || [];
      const newPath = newSnap.traversal_path || [];
      const traversal_path = newPath.length >= prevPath.length ? newPath : prevPath;
      return {
        ...newSnap,
        current_node_nid: newSnap.current_node_nid || prev.current_node_nid || null,
        visited_nodes,
        traversal_path,
        visited_node_details: { ...(prev.visited_node_details || {}), ...(newSnap.visited_node_details || {}) },
        diagnostic_subgraph: newSnap.diagnostic_subgraph || prev.diagnostic_subgraph,
      };
    });
    // Mini view only tracks the visited path. A full-graph-only snapshot (no
    // visited markers — e.g. served from semantic cache) must not pollute it.
    if (!(newSnap.visited_nodes || []).length && !newSnap.current_node_nid) return;
    setDiagMapSnapshot((prev) => {
      // Filter to visited-only even on the FIRST snapshot — the raw snapshot
      // carries the whole runbook subgraph, which belongs in the full view only.
      const newVisited = new Set([
        ...(newSnap.visited_nodes || []),
        ...(newSnap.traversal_path || []),
        ...(newSnap.current_node_nid ? [newSnap.current_node_nid] : []),
      ]);

      const nodeMap = new Map();
      (prev?.diagnostic_subgraph?.nodes || []).forEach(n => { if (n.nid) nodeMap.set(n.nid, n); });
      (newSnap.diagnostic_subgraph?.nodes || [])
        .filter(n => n.nid && (newVisited.size === 0 || newVisited.has(n.nid)))
        .forEach(n => nodeMap.set(n.nid, n));

      const edgeMap = new Map();
      const eKey = (e) => `${e.from_nid ?? e.from}|${e.to_nid ?? e.to}`;
      (prev?.diagnostic_subgraph?.edges || []).forEach(e => edgeMap.set(eKey(e), e));
      (newSnap.diagnostic_subgraph?.edges || [])
        .filter(e => {
          const f = e.from_nid ?? e.from;
          const t = e.to_nid ?? e.to;
          return nodeMap.has(f) && nodeMap.has(t);
        })
        .forEach(e => edgeMap.set(eKey(e), e));

      const visited = [...new Set([...(prev?.visited_nodes || []), ...(newSnap.visited_nodes || [])])];
      const prevPath = prev?.traversal_path || [];
      const newPath = newSnap.traversal_path || [];
      const traversal_path = newPath.length >= prevPath.length ? newPath : prevPath;

      return {
        ...newSnap,
        visited_nodes: visited,
        traversal_path,
        visited_node_details: { ...(prev?.visited_node_details || {}), ...(newSnap.visited_node_details || {}) },
        diagnostic_subgraph: {
          nodes: [...nodeMap.values()],
          edges: [...edgeMap.values()],
        },
      };
    });
  }, []);

  const clearDiagMap = useCallback(() => {
    setDiagMapTrail([]);
    setDiagMapSnapshot(null);
    setDiagMapFullSnapshot(null);
    diagMapTurnRef.current = 0;
    setDiagMapMaxTurn(null);
    setDiagMapInlineOpen(false);
    setDiagMapInlineMinimized(false);
    setDiagMapFullOpen(false);
    setDiagMapFullMinimized(false);
    setDiagMapFocusNodeId(null);
    setDiagMapFocusKey(0);
    setDiagMapFullFocusNodeId(null);
    setDiagMapFullFocusKey(0);
  }, []);

  diagMapOpenRef.current = diagMapInlineOpen || diagMapFullOpen;

  // ── Background fetch: load the full knowledge graph for a product ───────
  // Cache key = graphVersion (hash of mermaid files) when available, else product.
  // Fetches once per session; refetches only if the mermaid version changes.
  const fetchKnowledgeGraph = useCallback(({ product, graphVersion } = {}) => {
    if (!product) return;
    const cacheKey = graphVersion || product;
    if (graphFetchedForProductRef.current === cacheKey && fullGraphRef.current) return;
    console.log("[KG] Fetching knowledge graph for product:", product, "version:", graphVersion);
    graphFetchedForProductRef.current = cacheKey;
    setGraphLoading(true);
    authFetch(KNOWLEDGE_GRAPH_ENDPOINT(product))
      .then((res) => {
        if (!res.ok) throw new Error(`KG fetch failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const subgraph = data.diagnostic_subgraph || data;
        if ((subgraph.nodes || []).length) {
          fullGraphRef.current = { nodes: subgraph.nodes, edges: subgraph.edges || [] };
          console.log(`[KG] Cached graph for ${product}: ${subgraph.nodes.length} nodes, ${(subgraph.edges || []).length} edges`);
          if (lastChatDataRef.current) {
            const snap = {
              diagnostic_subgraph: { nodes: subgraph.nodes, edges: subgraph.edges || [] },
              current_node_nid: lastChatDataRef.current.current_node_nid || null,
              traversal_path: lastChatDataRef.current.traversal_path || [],
              visited_nodes: lastChatDataRef.current.traversal_path || [],
              visited_node_details: lastChatDataRef.current.visited_node_details || {},
              eliminated_causes: lastChatDataRef.current.eliminated_causes || [],
            };
            advanceDiagMapSnapshot(snap);
          }
        } else {
          console.warn("[KG] Empty graph returned for product:", product);
          graphFetchedForProductRef.current = null;
        }
      })
      .catch((err) => {
        console.warn("[KG] Failed to fetch knowledge graph:", err.message);
        graphFetchedForProductRef.current = null;
      })
      .finally(() => setGraphLoading(false));
  }, [advanceDiagMapSnapshot]);

  // ── Build a synthetic snapshot from cached graph + chat response fields ──
  const buildSyntheticSnapshot = useCallback((payload) => {
    const graph = fullGraphRef.current;
    if (!graph && !payload.current_node_nid && !payload.traversal_path) return null;
    return {
      diagnostic_subgraph: graph
        ? { nodes: graph.nodes, edges: graph.edges }
        : undefined,
      current_node_nid: payload.current_node_nid || null,
      traversal_path: payload.traversal_path || [],
      visited_nodes: payload.traversal_path || [],
      visited_node_details: payload.visited_node_details || {},
      eliminated_causes: payload.eliminated_causes || [],
    };
  }, []);

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      if (!isResizingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.min(Math.max(pct, 20), 80));
    };
    const onUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const formatTimestamp = (ts) =>
    new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  const formatBotMessage = useCallback((text) => {
    if (!text) return "";
    // The ::sources … ::end manifest is machine-readable source data for the
    // Dashboard5 source cards, never prose. This dashboard has no cards, so
    // strip the block and render the inline [S1] markers as plain superscripts.
    let t = String(text)
      .replace(/^[ \t]*::sources[ \t]*\r?\n[\s\S]*?^[ \t]*::end[ \t]*\r?\n?/gim, "")
      .replace(/^[ \t]*::sources[ \t]*\r?\n[\s\S]*$/im, "")
      .replace(/\[\s*(S\d+(?:\s*,\s*S\d+)*)\s*\]/gi, (_m, ids) =>
        `<sup class="text-[10px] text-gray-400 ml-0.5">${ids.replace(/S/gi, "")}</sup>`
      );
    t = t.split("\n").filter((l) => !/^\*\*Note\s*[-:]/.test(l.trim()) && !/^Note\s*:/i.test(l.trim()) && !/^\*\*TL;?DR\*?\*?/i.test(l.trim())).join("\n");

    // ── fenced / inline code ──────────────────────────────────────────────
    // Pull code out FIRST and park it behind placeholders. Every transform
    // below (bold, links, bare-URL autolink, "\n" → <br>) would otherwise
    // rewrite the inside of a code block — portal paths are full of "→", quotes
    // and URLs. Restored at the very end, HTML-escaped so it renders literally.
    const codeBlocks = [];
    const escapeHtml = (s) =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Distinct tokens per kind: a block may swallow the <br> around it (so it is
    // not padded by blank lines), an inline span must not (that would eat real
    // line breaks mid-paragraph). Token uses characters no transform below touches.
    const park = (html, kind) => {
      codeBlocks.push(html);
      return `@@CODE${kind}${codeBlocks.length - 1}@@`;
    };
    // ```lang\n ... ``` (lang optional, tolerate trailing spaces on the fence)
    t = t.replace(/```[ \t]*([a-zA-Z0-9_+-]*)[ \t]*\r?\n([\s\S]*?)```/g, (_m, _lang, code) =>
      park(
        '<pre class="bg-gray-100 border border-gray-300 rounded-md my-2 px-3 py-2 overflow-x-auto"' +
          ' style="font-size:12.5px;line-height:1.5"><code style="white-space:pre;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace">' +
          escapeHtml(code.replace(/\s+$/, "")) +
          "</code></pre>",
        "B"
      )
    );
    // an unterminated fence (streamed/truncated message) — don't leave ``` on screen
    t = t.replace(/```[ \t]*([a-zA-Z0-9_+-]*)[ \t]*\r?\n([\s\S]*)$/g, (_m, _lang, code) =>
      park(
        '<pre class="bg-gray-100 border border-gray-300 rounded-md my-2 px-3 py-2 overflow-x-auto"' +
          ' style="font-size:12.5px;line-height:1.5"><code style="white-space:pre;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace">' +
          escapeHtml(code.replace(/\s+$/, "")) +
          "</code></pre>",
        "B"
      )
    );
    // `inline code`
    t = t.replace(/`([^`\n]+)`/g, (_m, code) =>
      park(
        '<code class="bg-gray-100 border border-gray-200 rounded px-1" style="font-size:12.5px;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace">' +
          escapeHtml(code) +
          "</code>",
        "I"
      )
    );
    t = t.replace(/!\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g, '<a tabindex="0" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="$2" style="cursor:pointer;">$1</a>');
    t = t.replace(/!\[\]\[?(https?:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view[^\]]*)\]?/g, '<a tabindex="0" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="https://drive.google.com/file/d/$2/view?usp=sharing" style="cursor:pointer;">image</a>');
    t = t.replace(/\[image\]\((https?:\/\/[^)]+)\)/g, '<a tabindex="0" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="$1" style="cursor:pointer;">image</a>');
    t = t.replace(/^#{1,3}\s+/gm, "");
    t = t.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    t = t.replace(/\*(.*?)\*/g, "$1");
    t = t.replace(/^\s*[-•]\s+/gm, "  ");
    t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_, label, url) => {
      if (isImagePreviewLink(label, url)) return `<a tabindex="0" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="${url}" style="cursor:pointer;">${label}</a>`;
      if (isServerProxyDocUrl(url)) return `<a tabindex="0" class="citation-link text-blue-600 hover:text-blue-800 underline" data-doc-url="${url}" style="cursor:pointer;">${label}</a>`;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${label}</a>`;
    });
    t = t.replace(/(<strong>Source:<\/strong>\s*)(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">Source Document</a>');
    const urlRegex = /(?<!")\b(https?:\/\/(?:[^\s<>)"&]|&(?!lt;|gt;))+)(?!")/gi;
    t = t.replace(urlRegex, (m) => `<a href="${m}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${m}</a>`);
    t = t.replace(/(\|.+\|(?:\n\|[:\s\-|]+\|)?(?:\n\|.+\|)+)/g, (match) => {
      const lines = match.trim().split("\n").filter((l) => l.trim());
      if (lines.length < 2) return match;
      let html = '<table class="border-collapse border border-gray-300 my-3" style="width:100%;font-size:13px"><thead><tr>';
      lines[0].split("|").filter((c) => c.trim()).forEach((c) => { html += `<th class="border border-gray-300 px-3 py-2 bg-gray-100 font-semibold text-left">${c.trim()}</th>`; });
      html += "</tr></thead><tbody>";
      for (let i = 2; i < lines.length; i++) {
        html += "<tr>";
        lines[i].split("|").filter((c) => c.trim()).forEach((c) => { html += `<td class="border border-gray-300 px-3 py-2">${c.trim()}</td>`; });
        html += "</tr>";
      }
      return html + "</tbody></table>";
    });
    t = t.replace(
      /<strong>(What's Happening|Root Cause|Next Steps|Escalation Reality|Sources Selected|Recommended Path|Alternate Routes|Answer|Diagnosis Plan|My Pick:[^<]*|Recommended:[^<]*)<\/strong>/g,
      '<div style="margin-top:12px;margin-bottom:4px;padding:3px 10px;background:#f0f9ff;border-left:3px solid #0891b2;font-weight:600;font-size:13px;color:#155e75;border-radius:0 4px 4px 0;">$1</div>'
    );
    t = t.replace(/^(Diagnostic Procedure|Current State|Current Position|Interpretation|Next Step|After This|Root Cause|Available Fixes|Workaround Options|Permanent Fix|Important|What we are checking|Steps|What to Observe|Next|ROOT CAUSE|Resolution Options|Recommended immediate action):/gm, "<strong>$1:</strong>");
    t = t.replace(/^Response Type:.*$/gm, "");
    t = t.replace(/^---$/gm, '<hr class="my-3 border-gray-300">');
    t = t.replace(/\n/g, "<br>");
    // Restore code last — after "\n" → <br>, so <pre> keeps real newlines and does
    // not get double-spaced. Also drop a <br> directly around a block so the code
    // does not float in extra blank lines.
    if (codeBlocks.length) {
      t = t.replace(/(?:<br>)?\s*@@CODEB(\d+)@@\s*(?:<br>)?/g,
        (m, i) => codeBlocks[Number(i)] ?? m);
      t = t.replace(/@@CODEI(\d+)@@/g,
        (m, i) => codeBlocks[Number(i)] ?? m);
    }
    return t;
  }, []);

  // Socket setup — LEFT pane only (customer chat)
  useEffect(() => {
    const sock = connectSocket();
    socketRef.current = sock;
    const emitJoin = () => sock.emit("joinCopilotRoom", { room_id: "copilot_room", role: "agent", name: agentName, mode: "testpilot" });
    sock.on("connect", emitJoin);
    sock.on("reconnect", emitJoin);
    sock.on("disconnect", () => setCurrentSession(null));
    sock.on("newCopilotSession", (data) => {
      setDashboardMessages((prev) => [...prev, { text: `New Session Available: ${data.username} has entered copilot mode and is waiting for assistance. Click "Join Session" to connect.`, sender: "system", timestamp: Date.now(), sessionData: data }]);
    });
    sock.on("liveAgentMessage", (data) => {
      if (data.sender === "user" || data.sender === "customer") {
        setCustomerMessages((prev) => [...prev, { text: data.text, sender: data.sender || "user", agentName: data.agentName, userName: data.userName, timestamp: data.timestamp || Date.now() }]);
      }
    });
    if (sock.connected) emitJoin();
    return () => {
      socketRef.current = null;
      if (dashboardSendErrorTimerRef.current) window.clearTimeout(dashboardSendErrorTimerRef.current);
      sock.disconnect();
    };
  }, [agentName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (customerChatBodyRef.current) customerChatBodyRef.current.scrollTop = customerChatBodyRef.current.scrollHeight; }, [customerMessages]);
  useEffect(() => { if (latestCopilotMsgRef.current) { latestCopilotMsgRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); } else if (dashboardChatBodyRef.current) { dashboardChatBodyRef.current.scrollTop = dashboardChatBodyRef.current.scrollHeight; } }, [dashboardMessages, isThinking, isProcessing]);
  useEffect(() => {
    if (shouldScrollToDivider && clearDividerRef.current) {
      setTimeout(() => clearDividerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      setShouldScrollToDivider(false);
    }
  }, [shouldScrollToDivider, dashboardMessages]);

  useEffect(() => {
    const container = dashboardChatBodyRef.current;
    if (!container) return;
    const check = () => {
      const el = latestCopilotMsgRef.current;
      if (!el) { setShowScrollArrow(false); return; }
      const cRect = container.getBoundingClientRect();
      const mRect = el.getBoundingClientRect();
      setShowScrollArrow(mRect.bottom > cRect.bottom + 8);
    };
    requestAnimationFrame(() => requestAnimationFrame(check));
    container.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => { container.removeEventListener("scroll", check); window.removeEventListener("resize", check); };
  }, [dashboardMessages, currentQuestion]);

  const handleJoinSession = (sessionData) => {
    localStorage.setItem("CustomerName", sessionData.username);
    const s = socketRef.current;
    if (!s) return;
    s.emit("tseAgentJoinSession", { user_sid: sessionData.user_sid, session_room: sessionData.session_room, agent_name: agentName });
    setCurrentSession(sessionData);
    setDashboardMessages((prev) => [...prev, { text: `Joined Session: Successfully connected to ${capitalize(sessionData.username)}'s session. You can now assist them directly.`, sender: "system", timestamp: Date.now() }]);
  };

  const sendCustomerMessage = () => {
    const s = socketRef.current;
    if (!customerMessage.trim() || !s?.connected) return;
    setCustomerMessages((prev) => [...prev, { text: customerMessage, sender: "agent", timestamp: Date.now() }]);
    s.emit("agentMessage", { text: customerMessage, user_sid: currentSession?.user_sid || "all", target: "customer" });
    setCustomerMessage("");
    setTimeout(() => { if (customerTextareaRef.current) customerTextareaRef.current.style.height = "28px"; }, 0);
  };

  const handleCustomerKeyPress = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendCustomerMessage(); } };

  // REST call helper — uses authFetch (Cognito Bearer) to /api/copilot/chat proxy
  const callRestAPI = useCallback(async (text) => {
    if (copilotAbortRef.current) copilotAbortRef.current.abort();
    const ac = new AbortController();
    copilotAbortRef.current = ac;
    setIsThinking(true);
    setIsProcessing(false);
    setDashboardSendError("");
    try {
      const body = {
        messages: [{ role: "user", content: text }],
        mode: "copilot",
        ...(restSessionIdRef.current ? { session_id: restSessionIdRef.current } : {}),
      };
      const res = await authFetch("/api/copilot/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: ac.signal,
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        let detail = typeof e.detail === "string" ? e.detail.trim() : "";
        if (!detail && e.detail && typeof e.detail === "object") {
          detail = e.detail.message || "";
        }
        throw new Error(detail || `Server error ${res.status}`);
      }
      const payload = await res.json();
      console.log("[TSEDashboard2] API response payload:", {
        response_type: payload.response_type,
        waiting_for_clarification: payload.waiting_for_clarification,
        next_steps_count: payload.next_steps?.length,
        has_info_content: Boolean(payload.info_content),
        info_content_preview: payload.info_content?.slice?.(0, 120),
        response_preview: (payload.response || payload.content?.[0]?.text || "")?.slice?.(0, 120),
      });
      if (payload.session_id) restSessionIdRef.current = payload.session_id;
      if (payload.traversal_path?.length) lastChatDataRef.current = payload;
      // Trigger background graph fetch when product is known
      if (payload.selected_product) fetchKnowledgeGraph({ product: payload.selected_product, graphVersion: payload.graph_version });
      // Build synthetic snapshot from lightweight fields + cached graph.
      // The new API no longer sends session_snapshot / diagnostic_subgraph.
      const snapshot = buildSyntheticSnapshot(payload);
      if (snapshot) {
        advanceDiagMapSnapshot(snapshot);
      }
      let responseDiagMapNodeId = null;
      if (payload.current_node) {
        const diagNode = getDiagNodeFromCurrentNode(payload.current_node, { type: "Workflow" });
        responseDiagMapNodeId = diagNode?.nid || diagNode?.id || null;
        recordDiagMapNode(diagNode);
      } else if (snapshot?.current_node_nid) {
        responseDiagMapNodeId = snapshot.current_node_nid;
      }
      if (responseDiagMapNodeId && diagMapOpenRef.current) {
        setDiagMapFocusNodeId(responseDiagMapNodeId);
        setDiagMapFocusKey(k => k + 1);
      }
      const responseText = payload.response || payload.content?.[0]?.text || payload.message || payload.text || "(no response)";
      copilotAbortRef.current = null;
      setIsThinking(false);
      setIsProcessing(false);

      const normalizeOpt = (opt) => typeof opt === "string" ? opt : String(opt.label ?? opt.text ?? opt.value ?? JSON.stringify(opt));
      const isClarification = payload.waiting_for_clarification || payload.response_type === "clarification";
      const hasOptions = payload.next_steps?.length > 0;

      // Always render info_content when present, regardless of whether options exist
      if (isClarification && payload.info_content?.trim()) {
        console.log("[TSEDashboard2] Rendering info_content as bubble, length:", payload.info_content.length);
        setDashboardMessages((prev) => [...prev, {
          text: payload.info_content.trim(),
          sender: "copilot",
          timestamp: Date.now(),
          cache_hit: payload.cache_hit || false,
          diagMapNodeId: responseDiagMapNodeId,
        }]);
      }

      if (isClarification && hasOptions) {
        // The question widget receives only the question text (no info blob).
        // Strip any leftover option lines / boilerplate that the model may still
        // emit inside the question string.
        const optionLabels = new Set(
          payload.next_steps.map(normalizeOpt).map((s) => s.trim().toLowerCase())
        );
        const cleanQuestion = responseText
          .split("\n")
          .filter((line) => {
            const stripped = line.replace(/^\s*\d+[\.\)]\s*/, "").trim();
            if (optionLabels.has(stripped.toLowerCase())) return false;
            if (/^\s*\d+[\.\)]?\s*$/.test(line)) return false;
            return true;
          })
          .join("\n")
          .replace(/\breply with (option number|option number or exact option text|the option number|a number)[^\n.]*/gi, "")
          .replace(/\bselect (an option|one of the options|from the options)[^\n.]*/gi, "")
          .replace(/\(?(type|enter) (the )?(option )?(number|your (choice|answer|selection))[^\n.)]*\)?/gi, "")
          .replace(/\n{3,}/g, "\n\n")
          .replace(/\n\s*[.,:;]\s*$/g, "")
          .trimEnd();

        console.log("[TSEDashboard2] Rendering question widget:", cleanQuestion.slice(0, 120));
        setCurrentQuestion({
          question: cleanQuestion,
          options: payload.next_steps.map(normalizeOpt),
          input_type: payload.input_type || "single",
          messageId: Date.now(),
          cache_hit: payload.cache_hit || false,
        });
        recordDiagMapNode({
          id: `question-${Date.now()}`,
          type: "Decision",
          name: cleanQuestion,
          question: cleanQuestion,
        });
        setWaitingForAnswer(true);
        setIsQuestionCollapsed(false);
      } else if (!isClarification || !hasOptions) {
        // Regular response: add to chat bubble (no question widget)
        setCurrentQuestion(null);
        setWaitingForAnswer(false);
        setIsQuestionCollapsed(false);
        if (!payload.current_node) {
          const fallbackNode = {
            id: `response-${Date.now()}`,
            type: "Resolution",
            name: responseText,
            text: responseText,
          };
          responseDiagMapNodeId = fallbackNode.id;
          recordDiagMapNode(fallbackNode);
        }
        setDashboardMessages((prev) => [...prev, { text: responseText, sender: "copilot", timestamp: Date.now(), usage: payload.usage, cache_hit: payload.cache_hit || false, diagMapNodeId: responseDiagMapNodeId }]);
        // Session just closed — show the feedback reminder as its own blob,
        // separate from Claude's summary bubble above.
        if (payload.feedback_prompt) {
          setDashboardMessages((prev) => [...prev, { type: "feedback_prompt", text: payload.feedback_prompt, timestamp: Date.now() }]);
          // Session closed -> the Cost Breakdown table is now in the log; reconcile.
          const _sid = payload.session_id || restSessionIdRef.current;
          setTimeout(() => syncTurnBreakdownFromLogs(_sid), 1500);
        }
      }
      // Auto-trigger implicit search: cases on turns 3,6,9; bugs on turns 4,7,10
      copilotTurnRef.current += 1;
      const turn = copilotTurnRef.current;
      const sid = payload.session_id || restSessionIdRef.current || "";
      if (turn >= 3 && turn % 3 === 0 && sid) {
        authFetch("/api/copilot/implicit-search", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type: "cases", top_k: 5, session_id: sid }) })
          .then(r => r.json()).then(d => { if (d.cases?.length) { setImplicitCases(d.cases); setHasNewCases(true); } }).catch(() => {});
      }
      if (turn >= 4 && (turn - 1) % 3 === 0 && sid) {
        authFetch("/api/copilot/implicit-search", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type: "bugs", top_k: 5, session_id: sid }) })
          .then(r => r.json()).then(d => { if (d.bugs?.length) { setImplicitBugs(d.bugs); setHasNewBugs(true); } }).catch(() => {});
      }
      // Cost is shown ONLY from the db (authoritative). Live values cover latency
      // and turn count; cost stays 0 until the db reconcile fills it in. If the db
      // has no row for this turn (e.g. triage / cache), cost stays $0 — by design.
      setSessionStats((prev) => ({
        turns: prev.turns + 1,
        totalCostUsd: prev.totalCostUsd,
        totalLatencyMs: prev.totalLatencyMs + (payload.latency_ms || 0),
        turnLog: [...prev.turnLog, {
          n: prev.turns + 1,
          latencyMs: payload.latency_ms || 0,
          costUsd: 0,
          cacheHit: payload.cache_hit || false,
        }],
      }));
      // Reconcile cost with the db right after the turn (per-call rows are
      // committed server-side by the time we respond).
      {
        const _sid = payload.session_id || restSessionIdRef.current;
        if (_sid) setTimeout(() => syncTurnBreakdownFromLogs(_sid), 400);
      }
    } catch (err) {
      copilotAbortRef.current = null;
      setIsThinking(false);
      setIsProcessing(false);
      if (err.name === "AbortError") return;
      let errText = err.message || "Failed to get response.";
      try {
        const parsed = JSON.parse(errText);
        if (parsed.message) errText = parsed.message;
        if (parsed.request_id) errText += ` (request_id: ${parsed.request_id})`;
      } catch { }
      setDashboardMessages((prev) => [...prev, { text: `Error: ${errText}`, sender: "system", timestamp: Date.now() }]);
    }
  }, [recordDiagMapNode, advanceDiagMapSnapshot, fetchKnowledgeGraph, buildSyntheticSnapshot]);

  // Reconcile the turn-breakdown popover with the authoritative per-turn billing
  // logged in tse_agent/logs (Cost Breakdown table). Keeps live latency, uses
  // logged cost. Safe no-op while a session is still open (table not yet written).
  const syncTurnBreakdownFromLogs = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const res = await authFetch(`/api/tse/session/${encodeURIComponent(sid)}/turn-breakdown`);
      if (!res.ok) return;
      const data = await res.json();
      const logTurns = (data.turns || []).filter((t) => t.turn > 0);
      if (!logTurns.length) return;
      setSessionStats((prev) => {
        const byN = new Map((prev.turnLog || []).map((t) => [t.n, t]));
        const turnLog = logTurns.map((t) => ({
          n: t.turn,
          latencyMs: byN.get(t.turn)?.latencyMs || 0,   // latency not persisted in logs
          costUsd: t.cost_usd || 0,                       // authoritative from logs
          cacheHit: byN.get(t.turn)?.cacheHit || false,
        }));
        return {
          ...prev,
          turns: turnLog.length,
          totalCostUsd: data.total_cost_usd || turnLog.reduce((s, t) => s + t.costUsd, 0),
          turnLog,
        };
      });
    } catch { /* keep live values */ }
  }, []);

  const sendDashboardMessage = useCallback(async () => {
    if (!dashboardMessage.trim()) return;
    const trimmed = dashboardMessage.trim();
    const trimmedLower = trimmed.toLowerCase();
    if (trimmedLower === "/clear" || trimmedLower === "clear") {
      if (restSessionIdRef.current) {
        try { await authFetch(`/v1/sessions/${restSessionIdRef.current}`, { method: "DELETE" }); } catch { }
        restSessionIdRef.current = null;
      }
      firstPromptRef.current = null;
      clearDiagMap();
      resetSessionStats();
      setDashboardMessages((prev) => [...prev, { type: "session_clear_divider", sender: "system", timestamp: Date.now() }]);
      setShouldScrollToDivider(true);
      setCurrentQuestion(null); setWaitingForAnswer(false); setIsQuestionCollapsed(false); setDashboardMessage("");
      setTimeout(() => { if (dashboardTextareaRef.current) dashboardTextareaRef.current.style.height = "28px"; }, 0);
      return;
    }
    const questionSnapshot = currentQuestion;
    // Only use answered_question card when the typed text exactly matches one of the options.
    // Freeform text should always appear as a plain agent bubble so it's visible in chat.
    const matchedOption = questionSnapshot?.options?.find(
      (opt) => opt.trim().toLowerCase() === trimmed.toLowerCase()
    );
    const useQuestionCard = Boolean(matchedOption && questionSnapshot?.options?.length);
    if (!firstPromptRef.current) firstPromptRef.current = trimmed;
    setDashboardMessages((prev) => [...prev, {
      type: useQuestionCard ? "answered_question" : undefined,
      text: trimmed, sender: "agent", timestamp: Date.now(),
      questionSnapshot: useQuestionCard ? questionSnapshot : null,
      selectedAnswer: useQuestionCard ? matchedOption : null,
    }]);
    recordDiagMapNode({
      id: `agent-${Date.now()}`,
      type: "Symptom",
      name: trimmed,
      text: trimmed,
    });
    setCurrentQuestion(null); setWaitingForAnswer(false); setIsQuestionCollapsed(false); setDashboardMessage("");
    setTimeout(() => { if (dashboardTextareaRef.current) dashboardTextareaRef.current.style.height = "28px"; }, 0);
    await callRestAPI(trimmed);
  }, [dashboardMessage, currentQuestion, callRestAPI, clearDiagMap, recordDiagMapNode]);

  const sendQuickAction = useCallback((text) => {
    setDashboardMessages((prev) => [...prev, { text, sender: "agent", timestamp: Date.now() }]);
    setCurrentQuestion(null); setWaitingForAnswer(false); setIsQuestionCollapsed(false); setDashboardMessage("");
    setTimeout(() => { if (dashboardTextareaRef.current) dashboardTextareaRef.current.style.height = "28px"; }, 0);
    callRestAPI(text);
  }, [currentQuestion, callRestAPI]);

  const submitAnswer = useCallback((answer) => {
    const questionSnapshot = currentQuestion;
    setDashboardMessages((prev) => [...prev, {
      type: questionSnapshot?.options?.length ? "answered_question" : undefined,
      text: answer, sender: "agent", timestamp: Date.now(),
      questionSnapshot: questionSnapshot?.options?.length ? questionSnapshot : null,
      selectedAnswer: answer,
    }]);
    setCurrentQuestion(null); setWaitingForAnswer(false); setIsQuestionCollapsed(false); setDashboardMessage("");
    setTimeout(() => { if (dashboardTextareaRef.current) dashboardTextareaRef.current.style.height = "28px"; }, 0);
    callRestAPI(answer);
  }, [currentQuestion, callRestAPI]);

  const handleDashboardKeyPress = (e) => {
    if (e.nativeEvent.isComposing) return;
    if ((e.key === "Enter" || e.code === "Enter") && !e.shiftKey) { e.preventDefault(); sendDashboardMessage(); }
  };

  // Embedded auto-run: fire the provided query once on mount through the same
  // REST flow used by sendDashboardMessage (push the user bubble + callRestAPI).
  useEffect(() => {
    if (!embedded || !initialQuery || autoRanRef.current) return;
    const q = String(initialQuery).trim();
    if (!q) return;
    autoRanRef.current = true;
    if (!firstPromptRef.current) firstPromptRef.current = q;
    setDashboardMessages((prev) => [...prev, { text: q, sender: "agent", timestamp: Date.now() }]);
    recordDiagMapNode({ id: `agent-${Date.now()}`, type: "Symptom", name: q, text: q });
    callRestAPI(q);
  }, [embedded, initialQuery, callRestAPI, recordDiagMapNode]);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".relative")) { setShowCasesDropdown(false); setShowBugsDropdown(false); }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const autoResizeTextarea = (el) => { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 200) + "px"; };
  const handleImageClick = (url) => setZoomImageUrl(url);

  return (
    <div className={`flex w-full bg-white overflow-hidden ${embedded ? "h-full" : "min-h-screen h-screen"}`}>
      {!embedded && <SideBar mode="agent" onLogout={onLogout} isDarkMode={false} onToggleTheme={() => { }} onLinkClick={(p) => navigate(p)} currentPath={location.pathname} />}

      <div className="flex-1 flex flex-col h-full relative">
        {!embedded && <div className="absolute top-4 right-4 z-50"><UserProfileMenu onLogout={onLogout} /></div>}

        <div ref={splitContainerRef} className="flex h-full">

          {/* ── LEFT: Customer Chat (sockets) — hidden when embedded ───── */}
          {!embedded && (<>
            <div className="flex flex-col" style={{ width: `${leftPct}%`, minWidth: 0 }}>
              <div className="flex-1 flex flex-col bg-white h-full">
                <div className="bg-gray-50 border-b border-[#06b6d4] px-4 py-3 h-[76px] flex-shrink-0 flex flex-col justify-center">
                  <div className="flex justify-between items-center">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-lg font-semibold text-[#253439] truncate">Customer Chat</h2>
                      <p className="text-sm text-gray-600 truncate mt-0.5" title="Live conversation with customer">Live conversation with customer</p>
                    </div>
                    {currentSession && (
                      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                        <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <div className="w-2 h-2 bg-green-500 rounded-full" />
                          Connected to {capitalize(currentSession.username)}
                        </div>
                        <button onClick={() => { setCurrentSession(null); setCustomerMessages([]); setDashboardMessages((p) => [...p, { text: "Disconnected: Session ended.", sender: "system", timestamp: Date.now() }]); const s = socketRef.current; if (s?.connected) s.emit("clear_context_memory", { sessionId: currentSession?.id }); }} className="px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition">Disconnect</button>
                      </div>
                    )}
                  </div>
                </div>

                <div ref={customerChatBodyRef} className="flex-1 overflow-y-auto p-4 space-y-4" onClick={(e) => { const link = e.target.closest("a.image-link"); if (link) { e.preventDefault(); const u = link.getAttribute("data-image-url"); if (u) handleImageClick(u); return; } const doc = e.target.closest("a.citation-link"); if (doc) { e.preventDefault(); const du = doc.getAttribute("data-doc-url"); if (du) openAuthenticatedFile(du); } }}>
                  {customerMessages.length === 0 ? (
                    <div className="text-center text-gray-500 mt-8">
                      <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="mx-auto mb-4 text-gray-400"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                      <p>Start the conversation by joining a Copilot chat request!</p>
                    </div>
                  ) : customerMessages.map((msg, i) => (
                    <div key={i} className="w-full flex flex-col mb-4 items-start">
                      <div className={`px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm ${msg.sender === "agent" ? "bg-cyan-50 text-cyan-900 border-cyan-200" : msg.sender === "user" ? "bg-[#06b6d4] text-white border-[#06b6d4]" : "bg-white text-cyan-900 border-cyan-300"}`} style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "14px" }}>
                        <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }} />
                      </div>
                      <div className="mt-1 text-xs text-gray-400 font-medium">{msg.sender === "agent" ? `You ${formatTimestamp(msg.timestamp)}` : msg.sender === "user" ? `${capitalize(localStorage.getItem("CustomerName") || "")} ${formatTimestamp(msg.timestamp)}` : `Copilot ${formatTimestamp(msg.timestamp)}`}</div>
                    </div>
                  ))}
                </div>

                <div className={`bg-white rounded-xl border shadow-md flex items-start px-6 py-4 mx-4 mb-6 flex-shrink-0 ${currentSession ? "border-[#06b6d4]" : "border-gray-300"}`}>
                  <textarea ref={customerTextareaRef} className={`flex-1 bg-transparent border-none outline-none resize-none overflow-y-auto focus:ring-0 ${currentSession ? "text-[#253439] placeholder-[#A2A9AF]" : "text-gray-400 placeholder-gray-400 cursor-not-allowed"}`} style={{ minHeight: "28px", maxHeight: "200px", height: "28px", fontFamily: "'Space Grotesk', sans-serif", fontSize: "14px" }} placeholder={currentSession ? "Ask follow-up, or type a new question..." : "Connect to a session to start chatting..."} value={customerMessage} onChange={(e) => { if (currentSession) { setCustomerMessage(e.target.value); autoResizeTextarea(e.target); } }} onKeyDown={handleCustomerKeyPress} disabled={!currentSession} />
                  <button className={`ml-2 rounded-lg p-2 transition ${currentSession ? "bg-[#06b6d4] hover:bg-cyan-700" : "bg-gray-300 cursor-not-allowed"}`} onClick={sendCustomerMessage} disabled={!currentSession}>
                    <svg width="20" height="20" fill="none" stroke={currentSession ? "white" : "gray"} strokeWidth="2" viewBox="0 0 24 24"><path d="M22 2 11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Drag handle */}
            <div onMouseDown={handleResizeMouseDown} className="w-px flex-shrink-0 bg-transparent cursor-col-resize relative group" title="Drag to resize">
              <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gray-300/30 group-hover:bg-cyan-400/60 transition-colors duration-150" />
            </div>
          </>)}

          {/* ── RIGHT: Copilot Dashboard (REST) ───────────────────────── */}
          <div className="relative flex flex-col h-full" style={{ width: embedded ? "100%" : `${100 - leftPct}%`, minWidth: 0 }}>
            {!embedded && (
              <div className="bg-gray-50 border-b border-[#06b6d4] px-4 py-3 h-[76px] flex-shrink-0 flex flex-col justify-center">
                <div>
                  <h2 className="text-lg font-semibold text-[#253439] truncate">Copilot Dashboard</h2>
                  <div className="flex items-center gap-2 mt-0.5 text-sm min-w-0">
                    <p className="text-sm text-gray-500 truncate" title="AI-powered assistance for TSE">AI-powered assistance for TSE</p>
                    {sessionStats.turns > 0 && window.location.hostname === "zscaler.artasupport.com" && (
                      <>
                        <span className="text-gray-300 text-xs select-none">·</span>
                        <span className="relative group flex-shrink-0 cursor-help">
                          <span className="text-[10px] font-mono text-gray-400 whitespace-nowrap underline decoration-dotted decoration-gray-300 underline-offset-2">
                            {sessionStats.turns} turn{sessionStats.turns !== 1 ? "s" : ""}
                            {sessionStats.totalLatencyMs > 0 && ` · ${(sessionStats.totalLatencyMs / 1000).toFixed(1)}s`}
                            {sessionStats.totalCostUsd > 0 && ` · $${sessionStats.totalCostUsd.toFixed(4)}`}
                          </span>
                          <div className="absolute left-0 top-5 z-50 hidden group-hover:block w-52 bg-white border border-cyan-100 rounded-xl shadow-xl py-2 text-[10px] font-mono">
                            <div className="px-3 pb-1.5 border-b border-cyan-100 text-[9px] uppercase tracking-wider text-[#06b6d4] font-semibold">Turn breakdown</div>
                            {sessionStats.turnLog.map((t) => (
                              <div key={t.n} className="flex items-center justify-between px-3 py-1 hover:bg-cyan-50">
                                <span className="text-gray-400">#{t.n}{t.cacheHit ? " ⚡" : ""}</span>
                                <span className="text-[#253439]">{t.latencyMs > 0 ? `${(t.latencyMs / 1000).toFixed(1)}s` : "—"}</span>
                                <span className="text-[#06b6d4] font-semibold">{t.costUsd > 0 ? `$${t.costUsd.toFixed(4)}` : "—"}</span>
                              </div>
                            ))}
                          </div>
                        </span>
                        {dashboardMessages.length > 0 && (
                          <>
                            <span className="text-gray-300 text-xs select-none">·</span>
                            <button
                              onClick={() => downloadSessionMarkdown(dashboardMessages, sessionStats)}
                              className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#06b6d4] hover:bg-cyan-50 px-1.5 py-0.5 rounded transition whitespace-nowrap"
                              title="Export conversation as Markdown"
                            >
                              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M6 20h12a2 2 0 002-2V8l-6-6H6a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              Export .md
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex flex-wrap gap-2">
                {[{ a: "find_bugs", l: "Find Bugs" }, { a: "find_cases", l: "Find Cases" }, { a: "next_step", l: "Next Step", disabled: true }, { a: "possible_causes", l: "Possible Causes" }, { a: "analyze_cmd_output", l: "Analyze Cmd Output", disabled: true }].map(({ a, l, disabled }) => (
                  <button key={a} onClick={disabled ? undefined : () => sendQuickAction(l)} disabled={disabled} className={`px-2 py-1.5 border rounded-md text-xs font-medium transition ${disabled ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed" : "bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border-cyan-200"}`}>{l}</button>
                ))}
                <button onClick={() => sendQuickAction("Are there any active Zscaler service incidents right now?")} className="px-2 py-1.5 border rounded-md text-xs font-medium transition bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border-cyan-200">Active Incident</button>
                {!embedded && (
                  <button onClick={() => window.open(`${window.location.origin}/TseAgentRag`, "_blank", "width=1200,height=800,scrollbars=yes,resizable=yes")} className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition">Search</button>
                )}
                <button onClick={openDiagMapFullWindow} className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition flex items-center gap-1" type="button">
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="5" r="3" /><circle cx="5" cy="19" r="3" /><circle cx="19" cy="19" r="3" />
                    <path d="M12 8v3M7.5 17.5L10.5 11M16.5 17.5L13.5 11" />
                  </svg>
                  Diag Map
                </button>
                <button
                  onClick={openFeedbackModal}
                  className="px-2 py-1.5 bg-[#06b6d4] hover:bg-cyan-600 text-white rounded-md text-xs font-medium transition flex items-center gap-1"
                  type="button"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  Feedback
                </button>

                {implicitBugs.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={() => { setShowBugsDropdown(v => !v); setShowCasesDropdown(false); if (hasNewBugs) setHasNewBugs(false); }}
                      className="px-2 py-1.5 border rounded-md text-xs font-medium flex items-center gap-1 transition-colors duration-200 bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100"
                    >
                      Bugs
                      <span className={`min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white ${hasNewBugs ? "bg-orange-500 animate-pulse" : "bg-orange-400"}`}>{implicitBugs.length}</span>
                    </button>
                    {showBugsDropdown && (
                      <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                        <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 sticky top-0 bg-white rounded-t-xl">Known Bugs ({implicitBugs.length})</div>
                        {implicitBugs.map((bug, i) => (
                          <div key={i} className="px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-2">
                              {bug.url ? <a href={bug.url} target="_blank" rel="noreferrer" className="font-mono text-purple-700 font-semibold text-xs hover:underline">{bug.bug_id}</a> : <span className="font-mono text-purple-700 font-semibold text-xs">{bug.bug_id}</span>}
                              {bug.status && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{bug.status}</span>}
                              {bug.priority && <span className="text-[10px] text-gray-400">{bug.priority}</span>}
                              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 font-medium">{bug.match_pct}%</span>
                            </div>
                            {bug.summary && <p className="text-xs text-gray-600 line-clamp-2">{bug.summary}</p>}
                          </div>
                        ))}
                        <div className="px-3 py-2 text-center cursor-pointer hover:bg-orange-50 transition-colors rounded-b-xl" onClick={() => { if (loadingMoreBugs) return; setLoadingMoreBugs(true); authFetch("/api/copilot/implicit-search", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type: "bugs", top_k: implicitBugs.length + 5, session_id: restSessionIdRef.current || "" }) }).then(r => r.json()).then(d => { if (d.bugs?.length) setImplicitBugs(d.bugs); setLoadingMoreBugs(false); }).catch(() => setLoadingMoreBugs(false)); }}>
                          {loadingMoreBugs ? <span className="text-[11px] text-orange-500 font-medium animate-pulse">Loading...</span> : <span className="text-[11px] text-orange-600 font-medium hover:underline">Load More</span>}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {implicitCases.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={() => { setShowCasesDropdown(v => !v); setShowBugsDropdown(false); if (hasNewCases) setHasNewCases(false); }}
                      className="px-2 py-1.5 border rounded-md text-xs font-medium flex items-center gap-1 transition-colors duration-200 bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100"
                    >
                      Cases
                      <span className={`min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white ${hasNewCases ? "bg-cyan-500 animate-pulse" : "bg-cyan-400"}`}>{implicitCases.length}</span>
                    </button>
                    {showCasesDropdown && (
                      <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                        <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 sticky top-0 bg-white rounded-t-xl">Past Cases ({implicitCases.length})</div>
                        {implicitCases.map((c, i) => (
                          <div key={i} className="px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-mono font-semibold text-cyan-700">{c.url ? <a href={c.url} target="_blank" rel="noreferrer" className="hover:underline">{c.case_number || c.case_id}</a> : (c.case_number || c.case_id)}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-600 font-medium">{c.match_pct}%</span>
                            </div>
                            <p className="text-xs text-gray-700 mt-0.5 line-clamp-2">{c.subject}</p>
                            {c.resolution && <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-1">Resolution: {c.resolution}</p>}
                          </div>
                        ))}
                        <div className="px-3 py-2 text-center cursor-pointer hover:bg-cyan-50 transition-colors rounded-b-xl" onClick={() => { if (loadingMoreCases) return; setLoadingMoreCases(true); authFetch("/api/copilot/implicit-search", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type: "cases", top_k: implicitCases.length + 5, session_id: restSessionIdRef.current || "" }) }).then(r => r.json()).then(d => { if (d.cases?.length) setImplicitCases(d.cases); setLoadingMoreCases(false); }).catch(() => setLoadingMoreCases(false)); }}>
                          {loadingMoreCases ? <span className="text-[11px] text-cyan-500 font-medium animate-pulse">Loading...</span> : <span className="text-[11px] text-cyan-600 font-medium hover:underline">Load More</span>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {feedbackBlockedNotice && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 inline-block">
                  {feedbackBlockedNotice}
                </div>
              )}
            </div>

            <div ref={dashboardChatBodyRef} className="flex-1 overflow-y-auto p-4 space-y-4" onClick={(e) => { const link = e.target.closest("a.image-link"); if (link) { e.preventDefault(); const u = link.getAttribute("data-image-url"); if (u) handleImageClick(u); return; } const doc = e.target.closest("a.citation-link"); if (doc) { e.preventDefault(); const du = doc.getAttribute("data-doc-url"); if (du) openAuthenticatedFile(du); } }}>
              {dashboardMessages.length === 0 && !isThinking ? (
                <div className="text-center text-gray-500 mt-8">
                  <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="mx-auto mb-4 text-gray-400"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                <>
                  {(() => { const _lastIdx = dashboardMessages.reduce((acc, m, i) => (m.sender === "copilot" || m.type === "info") ? i : acc, -1); latestCopilotMsgRef.current = null; return dashboardMessages.map((msg, index) => { const _isLatestCopilot = index === _lastIdx;
                    if (msg.type === "session_clear_divider") {
                      return (
                        <div key={index} ref={index === dashboardMessages.length - 1 ? clearDividerRef : null} className="w-full">
                          <div className="flex items-center gap-3 py-3 px-4 mb-2 bg-teal-50 border border-teal-200 rounded-xl text-teal-700 text-sm font-medium">
                            <span className="font-semibold">Conversation cleared!</span>
                            <span className="font-normal text-teal-600 ml-1">How can I help you today? Ask your next question and I'll start fresh.</span>
                          </div>
                          <div style={{ height: "calc(100vh - 260px)" }} />
                        </div>
                      );
                    }
                    if (msg.type === "rag_searching") {
                      return (
                        <div key={index} className="w-full flex items-center gap-2 mb-2 px-1">
                          <div className="flex items-center gap-1">{[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</div>
                          <span className="text-xs text-gray-400">{msg.tool === "search_past_cases" ? "Looking for Cases..." : "Searching..."}</span>
                        </div>
                      );
                    }
                    if (msg.type === "rag_results") return null;
                    if (msg.type === "answered_question" && msg.questionSnapshot) {
                      const { question, options = [] } = msg.questionSnapshot;
                      return (
                        <div key={index} className="w-full flex flex-col mb-4 items-start">
                          <div className="w-full rounded-[12px] border border-gray-150 bg-white px-6 py-2 shadow-sm">
                            <div className="text-[13px] leading-6 text-gray-800" style={{ fontFamily: "'Space Grotesk', sans-serif" }} dangerouslySetInnerHTML={{ __html: formatBotMessage(question) }} />
                            {options.length > 0 && (
                              <div className="mt-1 flex flex-col gap-2">
                                {options.map((opt, oi) => (
                                  <div key={oi} className={`rounded-[10px] border px-4 py-0.5 text-[13px] leading-7 transition ${opt === msg.selectedAnswer ? "border-[#06b6d4] bg-cyan-50 text-[#253439]" : "border-gray-200 bg-white text-[#253439]"}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }} dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }} />
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-gray-400 font-medium flex items-center gap-1">{copilotLabel} {formatTimestamp(msg.timestamp)}{msg.questionSnapshot?.cache_hit && <CacheIndicator />}</div>
                        </div>
                      );
                    }
                    if (msg.type === "info") {
                      return (
                        <div key={index} ref={_isLatestCopilot ? latestCopilotMsgRef : undefined} className="w-full flex flex-col mb-4 items-start">
                          <div className="px-5 py-3 rounded-2xl max-w-[85%] break-words border border-cyan-200 bg-cyan-50 text-cyan-900 shadow-sm" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13px", whiteSpace: "pre-line" }}>
                            <span dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }} />
                            {_isLatestCopilot && showScrollArrow && (
                              <div className="sticky bottom-0 -mx-5 -mb-3 pt-6 pb-3 flex justify-center rounded-b-2xl" style={{ background: 'linear-gradient(transparent, rgb(236 254 255) 50%)', zIndex: 10 }}>
                                <div
                                  className="w-9 h-9 bg-white border border-gray-300 rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:bg-gray-50"
                                  onClick={() => { if (dashboardChatBodyRef.current) dashboardChatBodyRef.current.scrollTo({ top: dashboardChatBodyRef.current.scrollHeight, behavior: 'smooth' }); }}
                                  title="Scroll down"
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><path d="M6 9l6 6 6-6" /></svg>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-gray-400 font-medium">{copilotLabel} {formatTimestamp(msg.timestamp)}</div>
                        </div>
                      );
                    }
                    if (msg.type === "feedback_prompt") {
                      return (
                        <div key={index} className="w-full flex flex-col mb-4 items-start">
                          <div className="px-5 py-3 rounded-2xl max-w-[85%] break-words border border-cyan-200 bg-cyan-50 text-cyan-900 shadow-sm flex items-center justify-between gap-3 flex-wrap" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13px" }}>
                            <span className="font-semibold">{msg.text}</span>
                            <button
                              onClick={openFeedbackModal}
                              className="px-3 py-1 bg-[#06b6d4] hover:bg-cyan-600 text-white rounded-md text-xs font-medium whitespace-nowrap transition-colors"
                              type="button"
                            >
                              Open Feedback Form
                            </button>
                          </div>
                          <div className="mt-1 text-xs text-gray-400 font-medium">{copilotLabel} {formatTimestamp(msg.timestamp)}</div>
                        </div>
                      );
                    }
                    const isCopilot = msg.sender === "copilot";
                    const isAgent = msg.sender === "agent";
                    // Hide the copy/send/edit actions in the embedded enterprise (/ai-agent) view only.
                    const shouldShowActions = !embedded && isCopilot && msg.text && !msg.text.includes("Action triggered:") && !msg.text.includes("Determining");
                    const shouldShowMapAction = isCopilot && msg.text;
                    return (
                      <div key={index} ref={_isLatestCopilot ? latestCopilotMsgRef : undefined} className="w-full flex flex-col mb-4 items-start">
                        <div className="flex w-full items-start gap-3">
                          <div className="flex max-w-[85%] flex-col items-start">
                            <div className={`w-full px-5 py-4 rounded-2xl break-words border shadow-sm ${isAgent ? "bg-[#06b6d4] text-white border-[#06b6d4]" : msg.sender === "user" ? "bg-white text-[#253439] border-gray-200" : "bg-white text-cyan-900 border-cyan-300"}`} style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "14px" }}>
                              <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.text) }} />
                              {_isLatestCopilot && isCopilot && showScrollArrow && (
                                <div className="sticky bottom-0 -mx-5 -mb-4 pt-6 pb-3 flex justify-center rounded-b-2xl" style={{ background: 'linear-gradient(transparent, white 50%)', zIndex: 10 }}>
                                  <div
                                    className="w-9 h-9 bg-white border border-gray-300 rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:bg-gray-50"
                                    onClick={() => { if (dashboardChatBodyRef.current) dashboardChatBodyRef.current.scrollTo({ top: dashboardChatBodyRef.current.scrollHeight, behavior: 'smooth' }); }}
                                    title="Scroll down"
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><path d="M6 9l6 6 6-6" /></svg>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="mt-1 text-xs text-gray-400 font-medium flex items-center gap-1.5">
                              <span>{isAgent ? `${agentName} ${formatTimestamp(msg.timestamp)}` : msg.sender === "user" ? `Customer ${formatTimestamp(msg.timestamp)}` : `${copilotLabel} ${formatTimestamp(msg.timestamp)}`}</span>
                              {msg.cache_hit && <CacheIndicator />}
                            </div>
                          </div>
                        </div>
                        {(shouldShowActions || shouldShowMapAction) && (
                          <div className="flex items-center mt-2 ml-2 w-full">
                            {shouldShowActions && (
                              <div className="flex gap-2">
                                <button onClick={() => navigator.clipboard.writeText(msg.text)} className="p-1.5 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-md transition" title="Copy message">
                                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                                </button>
                                <button onClick={() => { setCustomerMessages((prev) => [...prev, { text: msg.text, sender: "agent", timestamp: Date.now() }]); const s = socketRef.current; if (s?.connected) s.emit("agentMessage", { text: msg.text, user_sid: currentSession?.user_sid || "all", target: "customer" }); }} className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition" title="Send to customer">
                                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 2 11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>
                                </button>
                                <button onClick={() => { setCustomerMessage(msg.text); setTimeout(() => { const el = document.querySelector('textarea[placeholder*="Ask follow-up"]'); if (el) { el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, 200) + "px"; el.focus(); } }, 0); }} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition" title="Edit in customer chat">
                                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                </button>
                              </div>
                            )}
                            {shouldShowMapAction && (
                              <button onClick={() => openInlineDiagMapForNode(msg.diagMapNodeId)} className="ml-auto mr-4 p-1.5 shrink-0 rounded-md border border-cyan-300 text-cyan-600 transition hover:bg-cyan-50 hover:text-cyan-800" title="Show diagnostic map for this response" type="button">
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="5" r="3" /><circle cx="5" cy="19" r="3" /><circle cx="19" cy="19" r="3" /><path d="M12 8v3M7.5 17.5L10.5 11M16.5 17.5L13.5 11" /></svg>
                              </button>
                            )}
                          </div>
                        )}
                        {msg.sender === "system" && msg.sessionData && (
                          <div className="flex gap-2 mt-2 ml-2">
                            <button onClick={() => handleJoinSession(msg.sessionData)} className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white border border-green-600 rounded-md text-xs font-medium transition flex items-center gap-1">
                              <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10,17 15,12 10,7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>
                              Join Session
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  }); })()}
                  {isThinking && (
                    <div className="w-full flex items-center gap-2 mb-4 px-1">
                      <div className="flex items-center gap-1">{[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</div>
                      <span className="text-xs text-gray-400">{copilotLabel} is thinking…</span>
                      <button
                        className="ml-1 p-0.5 rounded hover:bg-gray-100 transition"
                        title="Stop"
                        onClick={() => { if (copilotAbortRef.current) { copilotAbortRef.current.abort(); copilotAbortRef.current = null; } setIsThinking(false); setIsProcessing(false); if (restSessionIdRef.current) authFetch("/api/copilot/stop", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: restSessionIdRef.current }) }).catch(() => {}); }}
                        type="button"
                      >
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="#9ca3af"><rect x="4" y="4" width="16" height="16" rx="2" /></svg>
                      </button>
                    </div>
                  )}
                  {isProcessing && (
                    <div className="w-full flex items-center gap-2 mb-4 px-1">
                      <div className="flex items-center gap-1">{[0, 150, 300].map((d) => <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</div>
                      <span className="text-xs text-gray-400">Processing…</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className={`bg-white rounded-xl border shadow-md flex flex-col px-6 py-4 mx-4 mb-6 flex-shrink-0 ${currentQuestion ? "border-[#06b6d4] border-2 ring-2 ring-cyan-100" : waitingForAnswer ? "border-gray-300" : "border-[#06b6d4]"}`}>
              {currentQuestion && (
                <div className="w-full mb-3 pb-3 border-b border-cyan-200 relative">
                  {isQuestionCollapsed ? (
                    /* ── Collapsed: slim bar so user can scroll/read chat above ── */
                    <div className="flex items-center justify-between gap-2 py-0.5">
                      <span className="text-xs text-gray-400 italic">Question pending — expand to answer</span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {currentQuestion.cache_hit && <CacheIndicator />}
                        <button onClick={() => setIsQuestionCollapsed(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition" title="Expand">
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 15l7-7 7 7" /></svg>
                        </button>
                        <button onClick={() => { setCurrentQuestion(null); setWaitingForAnswer(false); setIsQuestionCollapsed(false); }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition" title="Dismiss">
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* ── Expanded: full question + options ── */
                    <div className="overflow-y-auto" style={{ maxHeight: "55vh" }}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        {currentQuestion.question && (
                          <div className="text-sm text-gray-600 flex-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                            <span dangerouslySetInnerHTML={{ __html: formatBotMessage(currentQuestion.question) }} />
                          </div>
                        )}
                        <div className="flex items-center gap-1 flex-shrink-0 sticky top-0">
                          {currentQuestion.cache_hit && <CacheIndicator />}
                          <button onClick={() => setIsQuestionCollapsed(true)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition" title="Collapse">
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="rotate-180"><path d="M5 15l7-7 7 7" /></svg>
                          </button>
                          <button onClick={() => { setCurrentQuestion(null); setWaitingForAnswer(false); setIsQuestionCollapsed(false); }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition" title="Dismiss">
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                          </button>
                        </div>
                      </div>
                      {currentQuestion.input_type === "text" || !currentQuestion.options?.length
                        ? <TextQuestionWidget onSubmit={submitAnswer} />
                        : currentQuestion.input_type === "single"
                          ? <div className="flex flex-col gap-2">{currentQuestion.options.map((opt, idx) => (<button key={idx} onClick={() => submitAnswer(opt)} className="text-left px-4 py-1.5 border border-gray-200 rounded-lg text-sm text-[#253439] hover:border-[#06b6d4] hover:bg-cyan-50 transition" style={{ fontFamily: "'Space Grotesk', sans-serif" }}><div dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }} /></button>))}</div>
                          : <MultiSelectWidget options={currentQuestion.options} onSubmit={submitAnswer} formatBotMessage={formatBotMessage} />
                      }
                    </div>
                  )}
                </div>
              )}
              {dashboardSendError && <p className="text-sm text-red-600 mb-2">{dashboardSendError}</p>}
              <div className="flex items-start w-full">
                <textarea ref={dashboardTextareaRef} className={`flex-1 bg-transparent border-none outline-none placeholder-[#A2A9AF] focus:ring-0 resize-none overflow-y-auto ${isThinking || isProcessing ? 'text-gray-400 cursor-not-allowed' : 'text-[#253439]'}`} style={{ minHeight: "28px", maxHeight: "200px", height: "28px", fontFamily: "'Space Grotesk', sans-serif", fontSize: "14px" }} placeholder={isThinking || isProcessing ? "Copilot is responding…" : currentQuestion ? "Select an option above or type a clarifying question..." : "Ask follow-up, or use natural language like 'find bugs'..."} value={dashboardMessage} onChange={(e) => { setDashboardMessage(e.target.value); autoResizeTextarea(e.target); }} onKeyDown={handleDashboardKeyPress} disabled={isThinking || isProcessing} />
                <button className="ml-2 bg-[#06b6d4] hover:bg-cyan-700 rounded-lg p-2 transition" aria-label="Send" onClick={sendDashboardMessage} type="button" disabled={isThinking || isProcessing}>
                  <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 2 11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></svg>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {zoomImageUrl && <ImagePreviewModal imageUrl={zoomImageUrl} onClose={() => setZoomImageUrl(null)} />}
      {showFeedbackModal && (
        <FeedbackModal
          surface="engineer"
          sessionId={restSessionIdRef.current}
          username={agentName}
          firstPrompt={firstPromptRef.current}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}
      <DiagnosticMapWindow
        open={diagMapInlineOpen && !diagMapFullOpen}
        minimized={diagMapInlineMinimized}
        inline
        onOpen={() => setDiagMapInlineMinimized(false)}
        onMinimize={() => setDiagMapInlineMinimized(true)}
        onClose={() => { setDiagMapInlineOpen(false); setDiagMapInlineMinimized(false); }}
        trail={diagMapTrail}
        snapshot={diagMapSnapshot}
        fullSnapshot={diagMapFullSnapshot ?? diagMapSnapshot}
        focusNodeId={diagMapFocusNodeId}
        focusKey={diagMapFocusKey}
        maxTurn={diagMapMaxTurn}
        title="Diagnostic Map"
        isGraphLoading={graphLoading}
      />
      <DiagnosticMapWindow
        open={diagMapFullOpen}
        minimized={diagMapFullMinimized}
        onOpen={openDiagMapFullWindow}
        onMinimize={() => setDiagMapFullMinimized(true)}
        onClose={() => { setDiagMapFullOpen(false); setDiagMapFullMinimized(false); }}
        trail={diagMapTrail}
        snapshot={diagMapFullSnapshot ?? diagMapSnapshot}
        focusNodeId={diagMapFullFocusNodeId}
        focusKey={diagMapFullFocusKey}
        title="Diagnostic Map"
        isGraphLoading={graphLoading}
      />
    </div>
  );
};

export default TSEDashboard2;
